import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, PieChart, Pie, Cell, Legend } from "recharts";
import { UserCircle, Users, Layers, GraduationCap, CalendarCheck, CalendarX, Clock, Timer, Award, Wallet, TrendingDown, AlertTriangle, Info, HeartPulse } from "lucide-react";
import { toast } from "sonner";

const fcfa = (v) => `${Math.round(Number(v || 0)).toLocaleString("fr-FR")} FCFA`;
const p = (v) => (v == null ? "—" : `${v} %`);
const COMP_LABELS = { assiduite: "Assiduité (taux de présence 30 j)", performances: "Performances (moyenne /10 × 10)", heures: "Heures réalisées / prévues (mois)", finances: "Recouvrement (encaissé / dû)" };

function Kpi({ icon: Icon, label, value, sub, tone = "text-[#1F7A63]", testid }) {
  return (
    <div className="ek-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-[#5C736A]"><Icon className={`w-4 h-4 ${tone}`} />{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-xs text-[#5C736A] mt-0.5">{sub}</div>}
    </div>
  );
}

function HealthCard({ sante }) {
  const s = sante.score;
  const color = s == null ? "#5C736A" : s >= 75 ? "#1F7A63" : s >= 50 ? "#E6B800" : "#D93838";
  return (
    <div className="ek-card p-5 lg:col-span-2" data-testid="health-card">
      <div className="flex items-center gap-2 font-semibold"><HeartPulse className="w-5 h-5" style={{ color }} /> Indice de santé de l&apos;école</div>
      <div className="flex items-end gap-2 mt-3">
        <span className="text-5xl font-bold" style={{ fontFamily: "Outfit", color }} data-testid="health-score">{s ?? "—"}</span>
        <span className="text-[#5C736A] mb-1.5">/ 100</span>
      </div>
      <div className="mt-4 space-y-2.5">
        {Object.entries(sante.composantes).map(([k, v]) => (
          <div key={k}>
            <div className="flex justify-between text-xs"><span>{COMP_LABELS[k]} · {sante.poids[k]} %</span><span className="font-semibold">{v == null ? "non calculable" : `${v}/100`}</span></div>
            <div className="h-2 rounded-full bg-[#E2E8F0] mt-1 overflow-hidden"><div className="h-full rounded-full bg-[#1F7A63] transition-[width] duration-700" style={{ width: `${v ?? 0}%` }} /></div>
          </div>
        ))}
      </div>
      <p className="text-[11px] text-[#5C736A] mt-3">Moyenne pondérée des critères disponibles ; un critère sans données est exclu du calcul.</p>
    </div>
  );
}

function Alerts({ items }) {
  const tone = { danger: "bg-red-50 text-red-700 border-red-200", warn: "bg-[#FFF7D6] text-[#7A6100] border-[#E6B800]/40", info: "bg-[#E8F2EF] text-[#1F7A63] border-[#1F7A63]/20" };
  return (
    <div className="ek-card p-5" data-testid="director-alerts">
      <div className="font-semibold mb-3 flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-[#B38F00]" /> Alertes</div>
      <div className="space-y-2">
        {items.map((a, i) => <div key={i} className={`text-sm rounded-lg border px-3 py-2 ${tone[a.niveau]}`}>{a.texte}</div>)}
        {!items.length && <div className="text-sm text-[#5C736A]">Aucune alerte : tout est au vert.</div>}
      </div>
    </div>
  );
}

function Charts({ d, labels }) {
  const ev = Object.entries(d.evenements).map(([k, v]) => ({ name: labels[k], value: v })).filter((x) => x.value);
  const COLORS = ["#1F7A63", "#D93838", "#E6B800", "#5C736A", "#2F9C80", "#94A3B8"];
  return (
    <div className="grid lg:grid-cols-3 gap-4">
      <div className="ek-card p-4 lg:col-span-2">
        <div className="font-semibold text-sm mb-3">Présence et moyenne par classe</div>
        <div className="h-64">
          <ResponsiveContainer>
            <BarChart data={d.par_classe}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="classe" tick={{ fontSize: 11 }} /><YAxis yAxisId="l" domain={[0, 100]} tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" domain={[0, 10]} tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar yAxisId="l" dataKey="taux_presence" name="Présence %" fill="#1F7A63" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="r" dataKey="moyenne" name="Moyenne /10" fill="#E6B800" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
      <div className="ek-card p-4">
        <div className="font-semibold text-sm mb-3">Vie scolaire (30 jours)</div>
        <div className="h-64">
          {ev.length ? (
            <ResponsiveContainer>
              <PieChart><Pie data={ev} dataKey="value" nameKey="name" innerRadius={45} outerRadius={80}>{ev.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Pie><Tooltip /><Legend wrapperStyle={{ fontSize: 11 }} /></PieChart>
            </ResponsiveContainer>
          ) : <div className="h-full grid place-items-center text-sm text-[#5C736A]">Aucun événement</div>}
        </div>
      </div>
      <div className="ek-card p-4 lg:col-span-3">
        <div className="font-semibold text-sm mb-3">Recettes par mois</div>
        <div className="h-56">
          <ResponsiveContainer>
            <BarChart data={d.recettes_par_mois}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="mois" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} /><Tooltip formatter={(v) => fcfa(v)} /><Bar dataKey="recettes" name="Recettes" fill="#1F7A63" radius={[4, 4, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}

export default function DirecteurDashboard() {
  const { user, refresh } = useAuth();
  const [school, setSchool] = useState("");
  const [classId, setClassId] = useState("all");
  const [classes, setClasses] = useState([]);
  const [res, setRes] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setErr(null);
    api.get("/dashboard/directeur/overview", { params: { school: school || undefined, class_id: classId !== "all" ? classId : undefined } })
      .then(({ data }) => { setRes(data); if (!school && data.selected) setSchool(data.selected); })
      .catch((e) => setErr(formatError(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school, classId]);

  useEffect(() => { api.get("/classes").then(({ data }) => setClasses(data)).catch(() => {}); }, [user.school_id]);

  const pick = async (v) => {
    setRes(null);
    if (v !== "all" && v !== user.school_id) {
      try { await api.post("/me/active-school", { school_id: v }); await refresh(); } catch (e) { toast.error(formatError(e)); }
    }
    setSchool(v);
    setClassId("all");
  };

  if (err) return <div className="ek-card p-6 text-sm text-red-600" data-testid="director-dashboard-error">Impossible de charger le tableau de bord : {err}</div>;
  if (!res) return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{Array.from({ length: 8 }).map((_, i) => <div key={i} className="skel h-24" />)}</div>;
  if (!res.data) return <div className="ek-card p-6 text-sm text-[#5C736A]">Aucune école associée à votre compte.</div>;
  const d = res.data;

  return (
    <div className="space-y-5" data-testid="director-dashboard">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: "Outfit" }}>Bonjour, {user.name?.split(" ")[0]}</h1>
          <p className="text-sm text-[#5C736A] mt-1">Situation générale — {d.nom}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
        {school !== "all" && classes.length > 0 && (
          <Select value={classId} onValueChange={(v) => { setRes(null); setClassId(v); }}>
            <SelectTrigger className="w-56" data-testid="director-class-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Vue globale de l&apos;école</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>Classe {c.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        {res.schools.length > 1 && (
          <Select value={school} onValueChange={pick}>
            <SelectTrigger className="w-64" data-testid="director-school-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes mes écoles</SelectItem>
              {res.schools.map((s) => <SelectItem key={s.id} value={s.id}>{s.nom}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        </div>
      </div>

      <div className="ek-card p-4 flex gap-3 items-start bg-[#E8F2EF]/60" data-testid="director-summary">
        <Info className="w-5 h-5 text-[#1F7A63] shrink-0 mt-0.5" /><p className="text-sm leading-relaxed">{res.resume}</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={UserCircle} label="Élèves" value={d.eleves} sub={`${d.garcons} garçons · ${d.filles} filles`} testid="kpi-eleves" />
        <Kpi icon={GraduationCap} label="Enseignants" value={d.enseignants} tone="text-[#5C736A]" testid="kpi-enseignants" />
        <Kpi icon={Layers} label="Classes" value={d.classes} tone="text-[#5C736A]" testid="kpi-classes" />
        <Kpi icon={Award} label="Moyenne générale" value={d.moyenne == null ? "—" : `${d.moyenne}/10`} tone="text-[#B38F00]" testid="kpi-moyenne" />
        <Kpi icon={CalendarCheck} label="Taux de présence (30 j)" value={p(d.taux_presence)} sub={`${d.demi_journees_possibles} demi-journées possibles`} testid="kpi-presence" />
        <Kpi icon={CalendarX} label="Absences / taux" value={d.absences} sub={`Taux d'absence ${p(d.taux_absence)}`} tone="text-red-600" testid="kpi-absences" />
        <Kpi icon={Clock} label="Retards (30 j)" value={d.retards} tone="text-[#B38F00]" testid="kpi-retards" />
        <Kpi icon={Timer} label="Volume horaire (mois)" value={`${d.heures_realisees} / ${d.heures_dues} h`} sub={`Réalisé ${p(d.taux_heures)}`} testid="kpi-heures" />
        <Kpi icon={Wallet} label="Recettes du mois" value={fcfa(d.recettes_mois)} testid="kpi-recettes" />
        <Kpi icon={TrendingDown} label="Dépenses du mois" value={fcfa(d.depenses_mois)} tone="text-red-600" testid="kpi-depenses" />
        <Kpi icon={AlertTriangle} label="Impayés" value={fcfa(d.impayes)} sub={`${d.eleves_en_retard} élève(s) en retard`} tone="text-red-600" testid="kpi-impayes" />
        <Kpi icon={Users} label="Félicitations / difficultés" value={`${d.evenements.recompense} / ${d.evenements.difficulte}`} sub={`${d.evenements.comportement_positif} comportements positifs · ${d.evenements.comportement_negatif} négatifs`} testid="kpi-vie" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <HealthCard sante={res.sante} />
        <Alerts items={res.alertes} />
      </div>

      {res.par_ecole.length > 0 && (
        <div className="ek-card p-0 ek-table-wrap" data-testid="director-per-school">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-[#5C736A] border-b border-[#E2E8F0]"><th className="p-3">École</th><th className="p-3">Élèves</th><th className="p-3">Santé</th><th className="p-3">Présence</th><th className="p-3">Moyenne</th><th className="p-3">Impayés</th></tr></thead>
            <tbody>{res.par_ecole.map((s) => (
              <tr key={s.school_id} className="border-b border-[#E2E8F0] last:border-0"><td className="p-3 font-semibold">{s.nom}</td><td className="p-3">{s.eleves}</td><td className="p-3">{s.sante ?? "—"}/100</td><td className="p-3">{p(s.taux_presence)}</td><td className="p-3">{s.moyenne ?? "—"}</td><td className="p-3">{fcfa(s.impayes)}</td></tr>
            ))}</tbody>
          </table>
        </div>
      )}

      <Charts d={d} labels={res.evenement_labels} />
    </div>
  );
}
